import React, { useState, useCallback, useMemo, useEffect } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import { withStyles, Button, Typography } from '@material-ui/core';
import { useTranslation } from 'react-i18next';
import styles from './styles';
import EditIcon from '@material-ui/icons/Edit';
import EditingBanner from '../../../common/EditingBanner';
import NumberInput from './NumberInput';
import TimeLimits from './TimeLimits';
import SupportingLinks from './SupportingLinks';
import ArrowDropDownIcon from '@material-ui/icons/ArrowDropDown';
import PhotoUpload from '../../../common/PhotoUpload';
import {
    getFeatureFlagValue,
    REMOTE_CONFIG_SHOW_TIME_LIMITS,
} from '../../../utils/remoteConfig';
import { DEFAULT_TIME_LIMIT } from '../constants';

const MAX_KEYWORDS_LENGTH = 140;

const CreateActionForm = ({
    classes,
    title,
    onChangeValue,
    onSubmit,
    communityId,
    actionDuplicationCommunities,
    selectedTheme,
    initialData,
    onScrollTo,
}) => {
    const { t } = useTranslation();
    const [values, setValues] = useState({
        name:
            typeof initialData.name === 'object'
                ? initialData.name
                : { en: initialData.name || '', es: '' },
        description:
            typeof initialData.description === 'object'
                ? initialData.description
                : { en: initialData.description || '', es: '' },
        bonusPoints: initialData.bonusPoints || 1,
        logCountToMaster: initialData.logCountToMaster || 1,
        timeRequirements: Array.isArray(initialData.timeRequirements)
            ? initialData.timeRequirements
            : [],
        marketplaceVisibility: initialData.marketplaceVisibility || 'hidden',
        links: initialData.links || [],
        videoLinks: initialData.videoLinks || [],
        keywords: {
            en: Array.isArray(initialData.keywords)
                ? initialData.keywords.join(' ')
                : initialData.keywords?.en || '',
            es: initialData.keywords?.es || '',
        },
    });
    const [pendingScrollTarget, setPendingScrollTarget] = useState(null); // Track pending scroll target

    const isTimeLimitsEnabled = getFeatureFlagValue(
        REMOTE_CONFIG_SHOW_TIME_LIMITS,
    );

    useEffect(() => {
        if (pendingScrollTarget && onScrollTo) {
            onScrollTo(pendingScrollTarget); // Call onScrollTo after render
            setPendingScrollTarget(null); // Reset pending scroll target
        }
    }, [pendingScrollTarget, onScrollTo]);

    // eslint-disable-next-line no-unused-vars
    const [errors, setErrors] = useState({});
    const [icon, setIcon] = useState(initialData.icon || null);
    const [coverPhoto, setCoverPhoto] = useState(
        initialData.coverPhoto || null,
    );
    const [categories, setCategories] = useState([]);
    useEffect(() => {
        setCategories(
            selectedTheme && selectedTheme.categories
                ? selectedTheme.categories
                : [],
        );
    }, [selectedTheme]);

    const handleChange = useCallback(
        (field, index = null) => (event) => {
            let newValue;

            if (field === 'links' || field === 'videoLinks') {
                // Set the pending scroll target with the index
                setPendingScrollTarget(`${field}-${index}`);
                newValue = Array.isArray(event)
                    ? event
                    : [...values[field], event];
            } else {
                // Set the pending scroll target for other fields
                setPendingScrollTarget(field);
                newValue = event.target.value;
                if (field === 'keywords') {
                    newValue = event.target.value;
                    if (newValue.length > MAX_KEYWORDS_LENGTH) {
                        return;
                    }
                    const keywordsObject = {
                        en: newValue,
                        es: values.keywords.es,
                    };
                    setValues((prevValues) => ({
                        ...prevValues,
                        keywords: keywordsObject,
                    }));
                    if (typeof onChangeValue === 'function') {
                        onChangeValue({ keywords: keywordsObject });
                    }
                    return;
                } else {
                    newValue = event.target.value;
                }
            }

            setValues((prevValues) => {
                const updatedValues = {
                    ...prevValues,
                    [field]:
                        field === 'name' || field === 'description'
                            ? {
                                  ...(typeof prevValues[field] === 'object'
                                      ? prevValues[field]
                                      : { en: '', es: '' }),
                                  en: newValue,
                              }
                            : newValue,
                };
                onChangeValue({ [field]: updatedValues[field] });
                return updatedValues;
            });

            if (typeof onChangeValue === 'function') {
                if (field === 'keywords') {
                    // Convert keywords string to array before sending to parent
                    const keywordsArray = newValue
                        .split(' ')
                        .filter((keyword) => keyword.trim() !== '');
                    onChangeValue({ [field]: keywordsArray });
                } else {
                    onChangeValue({ [field]: newValue });
                }
            }

            // Clear the error for this field as the user is typing
            setErrors((prevErrors) => ({
                ...prevErrors,
                [field]: '',
            }));
        },
        [onChangeValue, values, setPendingScrollTarget],
    );

    const charactersLeft = useMemo(() => {
        return MAX_KEYWORDS_LENGTH - values.keywords.en.length;
    }, [values.keywords.en]);

    const handleImageAdd = (type) => (imageData) => {
        if (onScrollTo) {
            onScrollTo(type);
        }

        if (imageData && imageData[0]) {
            const file = imageData[0].file;
            const baseImageData = {
                name: file.name,
                sizes: [
                    {
                        name: file.name,
                        width: file.width || 80,
                        height: file.height || 80,
                        isOriginal: true,
                        url: file.url,
                        file: {
                            name: file.name,
                            url: file.url,
                            filesize: file.filesize,
                            md5: file.md5,
                            s3_reference: file.s3_reference,
                            created: file.created,
                        },
                    },
                ],
            };

            if (type === 'icon') {
                setIcon(baseImageData);
                setErrors((prevErrors) => ({
                    ...prevErrors,
                    icon: '',
                }));
                onChangeValue({ icon: baseImageData });
            } else if (type === 'coverPhoto') {
                const photoData = {
                    photoAuthorName: coverPhoto?.photoAuthorName || '',
                    photo: baseImageData,
                };
                setCoverPhoto(photoData);
                setErrors((prevErrors) => ({
                    ...prevErrors,
                    coverPhoto: '',
                }));
                onChangeValue({ coverPhoto: photoData });
            }
        } else {
            if (type === 'icon') {
                setIcon(null);
                onChangeValue({ icon: null });
            } else if (type === 'coverPhoto') {
                setCoverPhoto(null);
                onChangeValue({ coverPhoto: null });
            }
        }
    };

    const handlePhotoAuthorNameChange = (event) => {
        let newValue = event.target.value;
        if (newValue.length > 50) {
            newValue = newValue.slice(0, 50);
        }
        if (coverPhoto) {
            if (onScrollTo) {
                onScrollTo('photoAuthor');
            }
            const updatedCoverPhoto = {
                ...coverPhoto,
                photoAuthorName: newValue,
            };
            setCoverPhoto(updatedCoverPhoto);

            setErrors((prevErrors) => ({
                ...prevErrors,
                photoAuthorName: '',
            }));

            onChangeValue({ coverPhoto: updatedCoverPhoto });
        }
    };

    const validate = useCallback(() => {
        const newErrors = {};
        if (!values.name?.en?.trim())
            newErrors.name = t('actions.errors.nameRequired');
        if (!values.description?.en?.trim())
            newErrors.description = t('actions.errors.descriptionRequired');
        if (!icon.name?.trim()) {
            newErrors.icon = t('actions.errors.iconRequired');
        }
        if (!coverPhoto)
            newErrors.coverPhoto = t('actions.errors.coverImageRequired');
        if (!values.categoryId)
            newErrors.category = t('actions.errors.categoryRequired');

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    }, [values, icon, coverPhoto, t]);

    const handleSubmit = () => {
        const isValid = validate();
        if (isValid) {
            const formattedData = {
                ...values,
                icon,
                coverPhoto,
                keywords: {
                    en: values.keywords.en,
                    es: values.keywords.es || '',
                },
            };
            onSubmit(formattedData);
        }
    };

    const handleNumberChange = useCallback(
        (field, value) => {
            if (field === 'bonusPoints') {
                if (onScrollTo) {
                    onScrollTo('bonusPoints');
                }
            }
            setValues((prev) => ({
                ...prev,
                [field]: value,
            }));
            if (typeof onChangeValue === 'function') {
                onChangeValue({ [field]: value });
            }
        },
        [onChangeValue, onScrollTo],
    );

    const handleTimeLimitsChange = useCallback(
        (newTimeLimits) => {
            const formattedTimeLimits = newTimeLimits.map(
                ({ maxBuzzes, timePeriodSeconds }) => ({
                    maxBuzzes,
                    timePeriodSeconds,
                }),
            );
            setValues((prev) => ({
                ...prev,
                timeRequirements: formattedTimeLimits,
            }));
            if (typeof onChangeValue === 'function') {
                onChangeValue({ timeRequirements: formattedTimeLimits });
            }
        },
        [onChangeValue],
    );

    const handleCategoryChange = (event) => {
        const selectedCategoryId = event.target.value;

        setValues((prev) => ({
            ...prev,
            categoryId: selectedCategoryId,
        }));

        setErrors((prevErrors) => ({
            ...prevErrors,
            category: '',
        }));

        if (typeof onChangeValue === 'function') {
            onChangeValue({ categoryId: selectedCategoryId });
        }
    };

    return (
        <div className={classes.container}>
            <div className={classes.header}>
                <Typography variant="h6" className={classes.headerTitle}>
                    <EditIcon className={classes.headerIcon} />
                    {t('actions.create.customAction')}
                </Typography>
            </div>
            <div className={classes.editingBannerContainer}>
                <EditingBanner
                    className={classes.editingBanner}
                    actionDuplicationCommunities={actionDuplicationCommunities}
                    textAlign="left"
                />
            </div>
            <div className={classes.form}>
                <Typography variant="subtitle1" className={classes.fieldTitle}>
                    {t('actions.create.theme')}*
                </Typography>
                <Typography
                    variant="subtitle1"
                    className={classes.themeName}
                    data-intercom-target="Create Action Builder Theme Name"
                >
                    {selectedTheme?.name}
                </Typography>

                {errors.category && (
                    <Typography className={classes.errorText}>
                        {errors.category}
                    </Typography>
                )}
                <Typography variant="subtitle1" className={classes.fieldTitle}>
                    {t('actions.create.category')}*
                </Typography>
                <div className={classes.selectWrapper}>
                    <select
                        value={values.categoryId || ''}
                        onChange={handleCategoryChange}
                        className={classes.select}
                    >
                        <option value="" disabled>
                            {t('actions.create.category')}
                        </option>
                        {categories.map((category) => (
                            <option key={category.id} value={category.id}>
                                {category.name}
                            </option>
                        ))}
                    </select>
                    <ArrowDropDownIcon className={classes.selectArrow} />
                </div>

                {errors.name && (
                    <Typography className={classes.errorText}>
                        {errors.name}
                    </Typography>
                )}
                <Typography variant="subtitle1" className={classes.fieldTitle}>
                    {t('actions.create.name')}*
                </Typography>
                <input
                    type="text"
                    name="name"
                    placeholder={t('actions.create.name')}
                    className={classes.inputField}
                    onChange={handleChange('name')}
                    value={values.name.en}
                    maxLength={50}
                />

                {errors.description && (
                    <Typography className={classes.errorText}>
                        {errors.description}
                    </Typography>
                )}
                <Typography variant="subtitle1" className={classes.fieldTitle}>
                    {t('actions.create.description')}*
                </Typography>
                <textarea
                    name="description"
                    placeholder={t('actions.create.description')}
                    className={classes.textareaField}
                    onChange={handleChange('description')}
                    value={values.description.en}
                    rows={3}
                    maxLength={350}
                />

                <div className={classes.twoColumnRow}>
                    <div className={classes.halfWidthField}>
                        {errors.icon && (
                            <Typography className={classes.errorText}>
                                {errors.icon}
                            </Typography>
                        )}
                        <Typography
                            variant="subtitle1"
                            className={classes.fieldTitle}
                        >
                            {t('actions.create.icon')}*
                        </Typography>
                        <PhotoUpload
                            className={classes.photoUpload}
                            communityId={communityId}
                            onUpload={handleImageAdd('icon')}
                            fileTooLargeErrorMessage={t(
                                'fileTooLargeErrorMessage',
                                { imageSize: '400x400' },
                            )}
                            dataIntercomTarget="Create Action Builder Icon Upload"
                        />
                    </div>
                    <div className={classes.halfWidthField}>
                        {errors.coverPhoto && (
                            <Typography className={classes.errorText}>
                                {errors.coverPhoto}
                            </Typography>
                        )}
                        <Typography
                            variant="subtitle1"
                            className={classes.fieldTitle}
                        >
                            {t('actions.create.coverImage')}*
                        </Typography>
                        <PhotoUpload
                            className={classes.photoUpload}
                            communityId={communityId}
                            onUpload={handleImageAdd('coverPhoto')}
                            fileTooLargeErrorMessage={t(
                                'fileTooLargeErrorMessage',
                                { imageSize: '1920x1080' },
                            )}
                            CropProps={{
                                minWidth: 800,
                                minHeight: 500,
                                maxWidth: 1080,
                                maxHeight: 1920,
                            }}
                            initCrop={{
                                aspect: 16 / 9,
                                width: 500,
                                unit: 'px',
                            }}
                            dataIntercomTarget="Create Action Builder Cover Image Upload"
                        />
                    </div>
                </div>

                {errors.photoAuthorName && (
                    <Typography className={classes.errorText}>
                        {errors.photoAuthorName}
                    </Typography>
                )}
                <Typography variant="subtitle1" className={classes.fieldTitle}>
                    {t('actions.create.coverImageAuthor')}
                </Typography>
                <input
                    type="text"
                    name="photoAuthorName"
                    placeholder={t('actions.create.author')}
                    className={classes.inputField}
                    onChange={handlePhotoAuthorNameChange}
                    value={coverPhoto ? coverPhoto.photoAuthorName : ''}
                    maxLength={50}
                />

                <div className={classes.twoColumnRow}>
                    <div className={classes.halfWidthField}>
                        <Typography
                            variant="subtitle1"
                            className={classes.twoColumnFieldTitle}
                        >
                            {t('actions.create.bonusPoints')}*
                        </Typography>
                        <NumberInput
                            field="bonusPoints"
                            value={values.bonusPoints}
                            onChange={handleNumberChange}
                            min={0}
                        />
                    </div>
                    <div className={classes.halfWidthField}>
                        <Typography
                            variant="subtitle1"
                            className={classes.twoColumnFieldTitle}
                        >
                            {t('actions.create.logCountToMaster')}*
                        </Typography>
                        <NumberInput
                            field="logCountToMaster"
                            value={values.logCountToMaster}
                            onChange={handleNumberChange}
                            min={0}
                        />
                    </div>
                </div>

                {isTimeLimitsEnabled && (
                    <TimeLimits
                        onChange={handleTimeLimitsChange}
                        value={
                            values.timeRequirements.length > 0
                                ? values.timeRequirements.map(
                                      ({ maxBuzzes, timePeriodSeconds }) => {
                                          let unit = 'Minutes';
                                          let interval = timePeriodSeconds / 60;
                                          if (timePeriodSeconds % 86400 === 0) {
                                              unit = 'Days';
                                              interval =
                                                  timePeriodSeconds / 86400;
                                          } else if (
                                              timePeriodSeconds % 3600 ===
                                              0
                                          ) {
                                              unit = 'Hours';
                                              interval =
                                                  timePeriodSeconds / 3600;
                                          }
                                          return {
                                              maxBuzzes,
                                              interval,
                                              unit,
                                              timePeriodSeconds,
                                          };
                                      },
                                  )
                                : [DEFAULT_TIME_LIMIT]
                        }
                    />
                )}

                <Typography variant="subtitle1" className={classes.fieldTitle}>
                    {t('actions.create.supportingYoutube')}
                </Typography>
                <SupportingLinks
                    onChange={(event, index) =>
                        handleChange('videoLinks', index)(event)
                    }
                    value={values.videoLinks}
                />

                <Typography variant="subtitle1" className={classes.fieldTitle}>
                    {t('actions.create.supportingLinks')}
                </Typography>
                <SupportingLinks
                    onChange={(event, index) =>
                        handleChange('links', index)(event)
                    }
                    value={values.links}
                />

                <Typography variant="subtitle1" className={classes.fieldTitle}>
                    {t('actions.create.keywords')}
                </Typography>
                <textarea
                    name="keywords"
                    placeholder={t('actions.create.keywords')}
                    className={classes.textareaField}
                    onChange={handleChange('keywords')}
                    value={values.keywords.en}
                    rows={3}
                    maxLength={MAX_KEYWORDS_LENGTH}
                />
                <Typography variant="body2" className={classes.characterCount}>
                    <i>
                        {charactersLeft} {t('actions.create.charactersLeft')}
                    </i>
                </Typography>

                {errors.visibility && (
                    <Typography className={classes.errorText}>
                        {errors.visibility}
                    </Typography>
                )}
                <Typography variant="subtitle1" className={classes.fieldTitle}>
                    {t('actions.create.visibility')}*
                </Typography>
                <div className={classes.selectWrapper}>
                    <select
                        value={values.marketplaceVisibility}
                        onChange={handleChange('marketplaceVisibility')}
                        className={classes.select}
                    >
                        <option value="visible">
                            {t('actions.create.visible')}
                        </option>
                        <option value="hidden">
                            {t('actions.create.hidden')}
                        </option>
                        <option value="inviteOnly">
                            {t('actions.create.inviteOnly')}
                        </option>
                    </select>
                    <ArrowDropDownIcon
                        className={classes.selectArrow}
                        data-intercom-target="Create Action Button Visibility Dropdown"
                    />
                </div>
            </div>
            <div className={classes.buttonContainer}>
                <Button
                    onClick={handleSubmit}
                    className={classes.submitButton}
                    variant="contained"
                    color="primary"
                    fullWidth
                >
                    {t('actions.create.submitButton')}
                </Button>
            </div>
        </div>
    );
};

CreateActionForm.propTypes = {
    classes: PropTypes.object.isRequired,
    title: PropTypes.string,
    onChangeValue: PropTypes.func.isRequired,
    onSubmit: PropTypes.func.isRequired,
    communityId: PropTypes.string.isRequired,
    actionDuplicationCommunities: PropTypes.array,
    initialData: PropTypes.object,
    selectedTheme: PropTypes.object.isRequired,
    onScrollTo: PropTypes.func,
};

CreateActionForm.defaultProps = {
    actionDuplicationCommunities: [],
    initialData: {},
    onScrollTo: null,
    title: '',
};

const mapStateToProps = (state) => ({
    actionDuplicationCommunities: state.user.actionDuplicationCommunities || [],
});

const StyledCreateActionForm = withStyles(styles)(CreateActionForm);

export const ConnectedCreateActionForm = connect(mapStateToProps)(
    StyledCreateActionForm,
);

export default StyledCreateActionForm;
